import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid'
import { useSession } from '@/context/SessionContext'
import { useProfile } from '@/context/ProfileContext'
import { subscribeToMailerLite, MAILERLITE_INTENTS } from '@/lib/mailerlite'
import { verifyStripeSession } from '@/lib/queries/payment'
import { pixel } from '@/lib/pixel'
import { capture } from '@/lib/posthog'

const PLAN_LABELS = { founding: 'Founding Member', annual: 'Annual', monthly: 'Monthly' }
const PLAN_VALUES = { founding: 79.99, annual: 99.99, monthly: 12.49 }

export default function PaymentSuccess() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useSession()
  const { profile, refreshProfile } = useProfile()
  const mlSent = useRef(false)
  const verifyAttempted = useRef(false)

  const sessionId = searchParams.get('session_id')
  const [status, setStatus] = useState(sessionId ? 'loading' : 'no_session')
  const [plan, setPlan] = useState(null)

  // Verify the Stripe session. Extracted so the failed-state UI can retry.
  const runVerify = useCallback(async () => {
    if (!sessionId || !user) return
    setStatus('loading')
    try {
      const result = await verifyStripeSession(sessionId)
      if (result.verified) {
        setPlan(result.plan)
        setStatus('verified')
        await refreshProfile()
        if (PLAN_VALUES[result.plan]) {
          pixel.trackSubscribe(PLAN_VALUES[result.plan])
          capture('subscribe_completed', {
            value: PLAN_VALUES[result.plan],
            currency: 'USD',
            plan: result.plan,
          })
        }

        // Subscribe to MailerLite newsletter on verified payment (belt-and-suspenders;
        // stripe-webhook also auto-subscribes to Payment Thanks group)
        if (!mlSent.current && user.email) {
          mlSent.current = true
          subscribeToMailerLite(
            user.email,
            profile?.displayName || '',
            MAILERLITE_INTENTS.NEWSLETTER
          ).catch(() => {})
        }
      } else {
        setStatus('failed')
      }
    } catch {
      setStatus('failed')
    }
  }, [sessionId, user, refreshProfile, profile])

  useEffect(() => {
    if (!sessionId || !user || verifyAttempted.current) return
    verifyAttempted.current = true
    runVerify()
  }, [sessionId, user, runVerify])

  // Detect if this verified payment is part of an in-flight onboarding (V7
  // Stripe round-trip). When true, the user came from step 14 paywall and
  // needs to land at step 15 handoff — NOT /home. Mason 2026-05-17 UX:
  // user-initiated "Continue setup" button, no auto-redirect, so the
  // payment confirmation gets a breath before being thrown back into flow.
  const needsOnboardingReturn = status === 'verified' && profile && !profile.onboardingCompleted

  // Auto-redirect after verification — ONLY for users who already completed
  // onboarding (e.g., upgrading from /pricing). V7 onboarding return path
  // skips auto-redirect and shows a "Continue setup" button instead.
  useEffect(() => {
    if (status !== 'verified' || needsOnboardingReturn) return
    const timer = setTimeout(() => navigate('/home'), 5000)
    return () => clearTimeout(timer)
  }, [status, needsOnboardingReturn, navigate])

  // ── No session_id in URL ──
  if (status === 'no_session') {
    return (
      <section className="min-h-screen bg-[var(--bg-base)] flex items-center justify-center px-4">
        <div className="glass-panel p-10 max-w-md w-full text-center">
          <ExclamationTriangleIcon
            className="w-12 h-12 text-[var(--text-muted)] mx-auto mb-4"
            aria-hidden="true"
          />
          <h1 className="font-heading text-[var(--text-primary)] text-xl font-bold mb-3">
            Payment Not Found
          </h1>
          <p className="text-[var(--text-muted)] text-sm mb-6">
            We couldn't find a payment session. If you just subscribed, try refreshing. If this
            keeps happening, contact support.
          </p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => navigate('/home')}
              className="px-5 py-2.5 rounded-xl border border-[var(--glass-border)] text-[var(--text-primary)] font-semibold text-sm hover:bg-[var(--bg-surface-high)] transition-colors"
            >
              Go Home
            </button>
            <button
              onClick={() => navigate('/support')}
              className="px-5 py-2.5 rounded-xl bg-[var(--gold)] text-[var(--text-on-gold)] font-semibold text-sm hover:opacity-90 transition-opacity"
            >
              Contact Support
            </button>
          </div>
        </div>
      </section>
    )
  }

  // ── Loading / verifying ──
  if (status === 'loading') {
    return (
      <section className="min-h-screen bg-[var(--bg-base)] flex items-center justify-center px-4">
        <div className="glass-panel p-10 max-w-md w-full text-center">
          <div className="w-10 h-10 border-3 border-[var(--gold)] border-t-transparent rounded-full animate-spin mx-auto mb-6" />
          <h1 className="font-heading text-[var(--text-primary)] text-xl font-bold mb-2">
            Verifying Your Payment…
          </h1>
          <p className="text-[var(--text-muted)] text-sm">This only takes a moment.</p>
        </div>
      </section>
    )
  }

  // ── Verification failed ──
  if (status === 'failed') {
    return (
      <section className="min-h-screen bg-[var(--bg-base)] flex items-center justify-center px-4">
        <div className="glass-panel p-10 max-w-md w-full text-center">
          <ExclamationTriangleIcon
            className="w-12 h-12 text-amber-400 mx-auto mb-4"
            aria-hidden="true"
          />
          <h1 className="font-heading text-[var(--text-primary)] text-xl font-bold mb-3">
            Something Went Wrong
          </h1>
          <p className="text-[var(--text-muted)] text-sm mb-6">
            We received your payment but couldn't verify it automatically. Your access should
            activate within a few minutes. If it doesn't, please contact support.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              onClick={runVerify}
              className="px-5 py-2.5 rounded-xl bg-[var(--gold)] text-[var(--text-on-gold)] font-semibold text-sm hover:opacity-90 transition-opacity"
            >
              Retry verification
            </button>
            <button
              onClick={() => navigate('/home')}
              className="px-5 py-2.5 rounded-xl border border-[var(--glass-border)] text-[var(--text-primary)] font-semibold text-sm hover:bg-[var(--bg-surface-high)] transition-colors"
            >
              Go Home
            </button>
            <button
              onClick={() => navigate('/support')}
              className="px-5 py-2.5 rounded-xl border border-[var(--glass-border)] text-[var(--text-primary)] font-semibold text-sm hover:bg-[var(--bg-surface-high)] transition-colors"
            >
              Contact Support
            </button>
          </div>
        </div>
      </section>
    )
  }

  // ── Verified ──
  return (
    <section className="min-h-screen bg-[var(--bg-base)] flex items-center justify-center px-4">
      <div className="glass-panel p-10 max-w-md w-full text-center">
        <CheckCircleIcon className="w-14 h-14 text-[var(--gold)] mx-auto mb-5" aria-hidden="true" />
        <h1 className="font-heading text-[var(--text-primary)] text-2xl md:text-3xl font-bold mb-3">
          Welcome to Premium!
        </h1>
        {plan && PLAN_LABELS[plan] && (
          <p className="text-[var(--gold)] text-sm font-semibold mb-2">{PLAN_LABELS[plan]} Plan</p>
        )}
        <p className="text-[var(--text-muted)] text-sm leading-relaxed mb-2">
          {profile?.displayName ? `${profile.displayName}, your` : 'Your'} account has been
          upgraded. Full access to every feature is now unlocked.
        </p>
        {needsOnboardingReturn ? (
          <>
            {/* V7 Stripe round-trip: user came from onboarding step 14
                paywall. Bounce them back to step 15 handoff via the resume
                flag (bypasses the "already completed" guard since they
                haven't actually completed yet — completeOnboarding fires
                on step 15 click). Spec: v7-onboarding-production-migration. */}
            <p className="text-[var(--text-muted)] text-xs mb-8">One last step to get you in.</p>
            <button
              onClick={() => navigate('/onboarding?step=15&resume=true')}
              className="w-full py-3.5 rounded-xl bg-[var(--gold)] text-[var(--text-on-gold)] font-bold hover:opacity-90 transition-opacity"
            >
              Continue setup →
            </button>
          </>
        ) : (
          <>
            <p className="text-[var(--text-muted)] text-xs mb-8">
              Redirecting you to your dashboard in a moment…
            </p>
            <button
              onClick={() => navigate('/home')}
              className="w-full py-3.5 rounded-xl bg-[var(--gold)] text-[var(--text-on-gold)] font-bold hover:opacity-90 transition-opacity"
            >
              Go to Dashboard →
            </button>
          </>
        )}
      </div>
    </section>
  )
}
